import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import AdminPage from "components/template/adminpage";
import { IconButton, CircularProgress } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AttendanceTable from "components/attendance-table";
import { PageStyle } from "./theme";
import moment from "moment";
import {
  getEmployee,
  getAttendance,
  saveAttendance,
  deleteAttendance,
} from "lib/attendance-employee";
import BoxInfo from "components/box-info";
import { ATTENDANCE_TYPE } from "lib/attendance";
import EditAttendance from "./edit-attendance";
import DeleteAttendance from "./delete-attendance";

const getMonthDate = (months) => {
  const date = moment().add(months, "month");

  return {
    from: date.clone().startOf("month"),
    to: date.clone().endOf("month"),
  };
};

const AttendanceManagement = ({
  employee_id,
}) => {
  const [month, setMonth] = useState(0);
  const [loading, setLoading] = useState(false);
  const [attendance, setAttendance] = useState(null);
  const [employees, setEmployees] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deletingEmployee, setDeletingEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const handleOpenEdit = (emp, date = null , record = null) => {
    setEditingEmployee(emp);
    setSelectedDate(date);
    setSelectedRecord(record)
  };

  const selectMonthDate = useMemo(() => getMonthDate(month), [month]);

  useEffect(() => {
    const loadEmployees = async () => {
      setLoading(true);
      try {
        const result = await getEmployee(employee_id);
        setEmployees(result);
      } catch (e) {
        console.error("Errore caricamento impiegati", e);
      }
    };

    if (employee_id) {
      loadEmployees();
    }
  }, [employee_id]);

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      try {
        const result = await getAttendance(
          selectMonthDate.from.format("YYYY-MM-DD"),
          selectMonthDate.to.format("YYYY-MM-DD"),
          employee_id,
        );
        setAttendance(result);
      } catch (e) {
        console.error("Errore caricamento presenze", e);
      } finally {
        setLoading(false);
      }
    };

    // Eseguiamo la chiamata solo se abbiamo i dati necessari
    if (employee_id && employees !== null) {
      loadStats();
    }
  }, [
    month,
    employees,
    refreshTrigger,
    selectMonthDate.from,
    selectMonthDate.to,
    employee_id,
  ]);

  const handleSaveAttendance = async (attendanceList) => {
    setSaving(true);
    try {
      await saveAttendance(attendanceList);
      setRefreshTrigger((prev) => prev + 1);
      setEditingEmployee(null);
    } catch (error) {
      console.error("Errore durante il salvataggio", error);
    } finally {
      setSaving(false);
    }
  };

   const handleDeleteAttendance = async (attendanceList) => {
    setDeleting(true);
    try {
      await deleteAttendance(attendanceList);
      setRefreshTrigger((prev) => prev + 1);
      setDeletingEmployee(null);
      setEditingEmployee(null);
      setSelectedDate(null);
      setSelectedRecord(null);
    } catch (error) {
      console.error("Errore durante l'eliminazione", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>Gestione Presenze</title>
      </Helmet>
      <AdminPage title="Gestione Presenze">
        <PageStyle>
          <div className={"topbar"}>
            <IconButton onClick={() => setMonth(month - 1)}>
              <ArrowBackIosIcon />
            </IconButton>
            <div className={"week-selected"}>
              {selectMonthDate.to.format("MMMM")} &nbsp;
              {selectMonthDate.to.format("YYYY")}
            </div>
            <IconButton onClick={() => setMonth(month + 1)}>
              <ArrowForwardIosIcon />
            </IconButton>

            {ATTENDANCE_TYPE.map((item, index) => (
              <BoxInfo
                key={index}
                type={item.type}
                text={item.text}
                color={item.color}
              />
            ))}
          </div>
          {loading && (
            <div className={"loading"}>
              <CircularProgress />
            </div>
          )}
          {!loading && attendance && (
            <AttendanceTable
              employees={employees}
              data={attendance}
              timeRange={selectMonthDate}
              onEditClick={handleOpenEdit}
              onDeleteClick={(emp) => setDeletingEmployee(emp)}
            />
          )}

          {editingEmployee && (
            <EditAttendance
              employee={editingEmployee}
              selectedDate={selectedDate}
              attendanceRecord={selectedRecord}
              deleting={deleting} 
              saving={saving}
              onCancel={() => {
                setEditingEmployee(null);
                setSelectedDate(null);
              }}
              onSave={handleSaveAttendance}
              onDelete={handleDeleteAttendance}
            />
          )}

          {deletingEmployee && (
            <DeleteAttendance
              employee={deletingEmployee}
              deleting={deleting}
              onCancel={() => setDeletingEmployee(null)}
              onDelete={handleDeleteAttendance}
            />
          )}
        </PageStyle>
      </AdminPage>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    employee_id: state.auth.employee?.data?.id,
  };
};
export default connect(
  mapStateToProps,
)(AttendanceManagement);
